import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { assetUrl, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, Plus, Trash2, MapPin, Square, MousePointer2, QrCode, Printer, FileUp, Pencil, Save, X, Move, Sparkles, ChevronsUpDown, Check, Crop, Eraser, Undo2, Redo2 } from "lucide-react";
import FloorPlanCanvas from "@/components/FloorPlanCanvas";
import AreaHistoryDialog from "@/components/AreaHistoryDialog";
import SketchUploadDialog from "@/components/SketchUploadDialog";

function FactCombobox({ clients, value, onChange, allBranches }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const selected = clients.find((c) => c.id === value);
    const label = selected ? `${selected.name}${selected.trade_name ? ` — ${selected.trade_name}` : ""}` : "";
    const filtered = clients.filter((c) => {
        if (!search) return true;
        const q = search.toLowerCase();
        if (c.name?.toLowerCase().includes(q) || c.trade_name?.toLowerCase().includes(q) || c.client_code?.toLowerCase().includes(q)) return true;
        // Also search in branch names for this client
        if (allBranches) {
            return allBranches.some(b => b._client_id === c.id && (b.name?.toLowerCase().includes(q) || b.odoo_contact_code?.toLowerCase().includes(q)));
        }
        return false;
    });
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={open} className="min-w-[240px] justify-between font-normal truncate" data-testid="map-client-select">
                    <span className="truncate">{label || "Cliente facturación..."}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar cliente..." value={search} onValueChange={setSearch} data-testid="map-client-search" />
                    <CommandList>
                        <CommandEmpty>Sin resultados</CommandEmpty>
                        {filtered.map((c) => (
                            <CommandItem key={c.id} value={c.id} onSelect={() => { onChange(c.id); setOpen(false); setSearch(""); }}>
                                <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm truncate">{c.name}</span>
                                    {c.trade_name && <span className="text-xs text-muted-foreground truncate">{c.trade_name}</span>}
                                </div>
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function BranchCombobox({ branches, value, onChange, branchesWithPlans }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const selected = branches.find((b) => b.id === value);
    const label = selected ? `${selected.name}${selected.odoo_contact_code ? ` (${selected.odoo_contact_code})` : ""}` : "";
    const filtered = branches.filter((b) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return b.name?.toLowerCase().includes(q) || b.odoo_contact_code?.toLowerCase().includes(q) || b.address?.toLowerCase().includes(q);
    });
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={open} className="min-w-[240px] justify-between font-normal truncate" data-testid="map-branch-select">
                    <span className="truncate">{label || "Cliente servicio..."}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar sucursal..." value={search} onValueChange={setSearch} data-testid="map-branch-search" />
                    <CommandList>
                        <CommandEmpty>Sin sucursales</CommandEmpty>
                        {filtered.map((b) => {
                            const hasPlan = branchesWithPlans?.has(b.id);
                            return (
                                <CommandItem key={b.id} value={b.id} onSelect={() => { onChange(b.id); setOpen(false); setSearch(""); }}>
                                    {hasPlan
                                        ? <span className="mr-2 h-4 w-4 flex items-center justify-center rounded-full bg-green-500 text-white text-[9px] font-bold shrink-0">✓</span>
                                        : <span className="mr-2 h-4 w-4 shrink-0" />
                                    }
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-sm truncate">{b.name}</span>
                                        {b.odoo_contact_code && <span className="text-xs text-muted-foreground truncate">{b.odoo_contact_code}{b.address ? ` · ${b.address}` : ""}</span>}
                                    </div>
                                </CommandItem>
                            );
                        })}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

const ZONES = [
    { v: "verde",    l: "Zona Verde (perímetro exterior)", allow: ["cebadero_cebo"] },
    { v: "amarilla", l: "Zona Amarilla (puntos focales interiores)", allow: ["cebadero_cebo", "trampa", "lampara_uv"] },
    { v: "roja",     l: "Zona Roja (áreas de acceso al cliente)", allow: ["trampa", "lampara_uv"] },
];
const TYPES = {
    cebadero_cebo: "Cebadero con cebo",
    trampa: "Trampa de captura",
    lampara_uv: "Lámpara UV",
};
const TRAP_SUBTYPES = {
    gato_papel: "Gato papel",
    bandeja: "Bandeja",
    multicaptura: "Multicaptura",
    trex: "T-Rex",
    trampa_paso: "Trampa de paso",
};
const BAIT_SUBTYPES = {
    caja: "Cebadero de caja",
    tubo: "Cebadero de tubo",
    roca: "Cebadero roca",
};
const SUBTYPES_FOR = (deviceType) => {
    if (deviceType === "trampa") return TRAP_SUBTYPES;
    if (deviceType === "cebadero_cebo") return BAIT_SUBTYPES;
    return null;
};
const DEFAULT_SUBTYPE_FOR = (deviceType) => {
    if (deviceType === "trampa") return "trex";
    if (deviceType === "cebadero_cebo") return "caja";
    return "";
};
const ALL_SUBTYPE_LABELS = { ...TRAP_SUBTYPES, ...BAIT_SUBTYPES };

/** Given a point (px, py) in %, return the name of the area it falls into (considering L-shapes). */
function detectAreaAtPoint(areas, px, py) {
    if (!areas || !areas.length) return "";
    for (const a of areas) {
        // Check primary rect
        if (px >= a.x && px <= a.x + a.w && py >= a.y && py <= a.y + a.h) return a.name;
        // Check additional rects (L-shapes)
        if (a.rects) {
            for (const r of a.rects) {
                if (px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h) return a.name;
            }
        }
    }
    return "";
}

export default function AdminFloorPlans() {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [plans, setPlans] = useState([]);
    const [activePlan, setActivePlan] = useState(null);
    const [devices, setDevices] = useState([]);
    const [mode, setMode] = useState("view"); // view | place-device | move-devices | edit-devices | place-area | edit-area
    const [editingAreaDraft, setEditingAreaDraft] = useState(null); // currently selected area for repositioning
    const [editingAreas, setEditingAreas] = useState([]); // local copy of all areas during edit mode
    const [areasModified, setAreasModified] = useState(false);
    const [pendingPlace, setPendingPlace] = useState(null); // {x, y} after map click
    const [editingDevice, setEditingDevice] = useState(null);
    const [editingArea, setEditingArea] = useState(null);
    const [areaFindings, setAreaFindings] = useState([]);
    const [sketchOpen, setSketchOpen] = useState(false);
    const [cropMode, setCropMode] = useState(false);
    const [cropRect, setCropRect] = useState(null);
    const [manualCropMode, setManualCropMode] = useState(false);
    const [manualCropBox, setManualCropBox] = useState(null); // {x, y, w, h} in % of displayed image
    const [eraserSize, setEraserSize] = useState(2);
    const eraserCanvasRef = useRef(null);
    const [detectingAreas, setDetectingAreas] = useState(false);
    const cropImgRef = useRef(null);
    const csvInputRef = useRef(null);
    const planFileRef = useRef(null);
    const [deviceRequests, setDeviceRequests] = useState([]);

    // ── Undo/Redo history (per-session, per-plan) ──────────────────────────
    // Snapshot = { image_url, areas, devices }. Captured reactively after any
    // committed change (crop/padding/eraser/device place-move-delete/areas).
    const historyRef = useRef({ stack: [], pointer: -1, planId: null, isRestoring: false });
    const [historyTick, setHistoryTick] = useState(0);
    const [devicesPlanId, setDevicesPlanId] = useState(null); // plan id the `devices` state currently belongs to

    const makeSig = (image_url, areas, devs) => JSON.stringify([
        image_url || "",
        (areas || []).map((a) => [a.area_id, a.name, a.x, a.y, a.w, a.h, (a.rects || [])]),
        (devs || []).map((d) => [d.id, d.position_x, d.position_y, d.device_type, d.zone, d.trap_subtype, d.rotated]),
    ]);

    useEffect(() => {
        if (!activePlan) return;
        const h = historyRef.current;
        // Reset history immediately when switching to a different plan.
        if (h.planId !== activePlan.id) {
            h.planId = activePlan.id; h.stack = []; h.pointer = -1; h.isRestoring = false;
            setHistoryTick((t) => t + 1);
        }
        const planSnapshot = activePlan;
        const devSnapshot = devices;
        // Debounce so the activePlan→devices two-step update collapses into ONE snapshot.
        const t = setTimeout(() => {
            // Only capture once the `devices` state actually belongs to the active plan
            // (avoids an intermediate empty-devices snapshot while switching plans).
            if (devicesPlanId !== planSnapshot.id) return;
            if (h.isRestoring) { h.isRestoring = false; return; }
            const sig = makeSig(planSnapshot.image_url, planSnapshot.areas, devSnapshot);
            if (h.stack[h.pointer]?.sig === sig) return; // no real change
            h.stack = h.stack.slice(0, h.pointer + 1);
            h.stack.push({ sig, snap: { image_url: planSnapshot.image_url, areas: planSnapshot.areas || [], devices: devSnapshot } });
            if (h.stack.length > 40) h.stack.shift(); // cap history depth
            h.pointer = h.stack.length - 1;
            setHistoryTick((t2) => t2 + 1);
        }, 220);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePlan, devices, devicesPlanId]);

    const restoreSnapshot = async (snap) => {
        try {
            const { data } = await api.post(`/floor-plans/${activePlan.id}/restore-state`, {
                image_url: snap.image_url, areas: snap.areas, devices: snap.devices,
            });
            setActivePlan(data.plan);
            setDevices(data.devices || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo restaurar");
        }
    };

    const undoMap = async () => {
        const h = historyRef.current;
        if (h.pointer <= 0) return;
        h.pointer -= 1;
        h.isRestoring = true;
        await restoreSnapshot(h.stack[h.pointer].snap);
        setHistoryTick((t) => t + 1);
        toast.success("Deshecho");
    };

    const redoMap = async () => {
        const h = historyRef.current;
        if (h.pointer >= h.stack.length - 1) return;
        h.pointer += 1;
        h.isRestoring = true;
        await restoreSnapshot(h.stack[h.pointer].snap);
        setHistoryTick((t) => t + 1);
        toast.success("Rehecho");
    };

    const canUndo = historyRef.current.pointer > 0;
    const canRedo = historyRef.current.pointer < historyRef.current.stack.length - 1;


    // Latest area finding per area_id → status color
    const areaStatusByAreaId = useMemo(() => {
        const latestByArea = {};
        for (const f of areaFindings) {
            const prev = latestByArea[f.area_id];
            if (!prev || (f.created_at || "") > (prev.created_at || "")) {
                latestByArea[f.area_id] = f;
            }
        }
        const out = {};
        for (const [aid, f] of Object.entries(latestByArea)) {
            out[aid] = f.risk_level === "alto" ? "alert" : f.risk_level === "medio" ? "warning" : "ok";
        }
        return out;
    }, [areaFindings]);

    const [allBranches, setAllBranches] = useState([]); // for search enrichment

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data));
        // Load all branches in a single request for search enrichment
        api.get("/branches/all").then((r) => {
            const all = (r.data || []).map(b => ({ ...b, _client_id: b.client_id }));
            setAllBranches(all);
        }).catch(() => setAllBranches([]));
    }, []);

    const [branchesWithPlans, setBranchesWithPlans] = useState(new Set());

    // Load branches when client changes
    useEffect(() => {
        if (!clientId) { setBranches([]); setBranchId(""); setBranchesWithPlans(new Set()); return; }
        setBranchId("");
        setPlans([]);
        setActivePlan(null);
        // Load branches + which ones have plans, then default to a branch that HAS
        // plans so maps are never hidden behind an empty branch selection.
        Promise.all([
            api.get(`/branches?client_id=${clientId}`).then((r) => r.data || []).catch(() => []),
            api.get(`/floor-plans/branches-with-plans?client_id=${clientId}`).then((r) => new Set(r.data || [])).catch(() => new Set()),
        ]).then(([brs, withPlans]) => {
            setBranches(brs);
            setBranchesWithPlans(withPlans);
            // Prefer a branch that has plans. If plans exist but belong to a branch
            // that is no longer in the client's branch list (orphaned/deleted branch),
            // clear the branch filter so ALL client plans are shown instead of hiding them.
            const preferred = brs.find((b) => withPlans.has(b.id));
            if (preferred) setBranchId(preferred.id);
            else if (withPlans.size > 0) setBranchId("");
            else if (brs[0]) setBranchId(brs[0].id);
        });
    }, [clientId]);

    const loadPlans = async () => {
        if (!clientId) return;
        const params = branchId ? `client_id=${clientId}&branch_id=${branchId}` : `client_id=${clientId}`;
        const r = await api.get(`/floor-plans?${params}`);
        setPlans(r.data);
        // Prefer plans that match the selected branch (not orphan plans with no branch)
        const branchPlans = branchId ? r.data.filter(p => p.branch_id === branchId) : r.data;
        const preferred = branchPlans[0] || r.data[0];
        if (preferred && (!activePlan || activePlan.client_id !== clientId || activePlan.branch_id !== branchId)) {
            setActivePlan(preferred);
        } else if (activePlan) {
            const fresh = r.data.find((p) => p.id === activePlan.id);
            if (fresh) setActivePlan(fresh);
            else if (preferred) setActivePlan(preferred);
        }
        if (!r.data.length) setActivePlan(null);
    };

    const loadDevices = async () => {
        if (!activePlan) { setDevices([]); setAreaFindings([]); setDevicesPlanId(null); return; }
        const r = await api.get(`/pest-devices?client_id=${clientId}&floor_plan_id=${activePlan.id}`);
        setDevices(r.data);
        setDevicesPlanId(activePlan.id);
        try {
            const af = await api.get(`/area-findings?floor_plan_id=${activePlan.id}`);
            setAreaFindings(af.data);
        } catch (e) { setAreaFindings([]); }
    };

    useEffect(() => {
        loadPlans();
        // Load pending device requests for this client
        if (clientId) {
            api.get(`/device-requests?client_id=${clientId}&status=pending`).then(r => setDeviceRequests(r.data || [])).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, branchId]);
    useEffect(() => {
        loadDevices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePlan]);

    const selectedBranch = branches.find((b) => b.id === branchId);
    const defaultPlanName = selectedBranch?.name || (clients.find((c) => c.id === clientId) || {}).name || "Plano";

    const uploadPlan = async (file) => {
        if (!clientId || !file) return;
        const fd = new FormData();
        fd.append("client_id", clientId);
        if (branchId) fd.append("branch_id", branchId);
        fd.append("name", defaultPlanName);
        fd.append("file", file);
        try {
            const r = await api.post("/floor-plans", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Plano subido");
            setActivePlan(r.data);
            loadPlans();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir plano"); }
    };

    const deletePlan = async () => {
        if (!activePlan) return;
        if (!window.confirm(`Eliminar plano "${activePlan.name}" y todos sus dispositivos?`)) return;
        await api.delete(`/floor-plans/${activePlan.id}`);
        toast.success("Plano eliminado");
        setActivePlan(null);
        loadPlans();
    };

    const renamePlan = async () => {
        const newName = window.prompt("Nuevo nombre del plano:", activePlan?.name || "");
        if (!newName) return;
        await api.put(`/floor-plans/${activePlan.id}`, { name: newName });
        toast.success("Plano renombrado");
        loadPlans();
        setActivePlan((p) => ({ ...p, name: newName }));
    };

    const detectAreasForPlan = async () => {
        if (!activePlan) return;
        setDetectingAreas(true);
        try {
            const r = await api.post("/sketches/detect-for-plan", { floor_plan_id: activePlan.id });
            toast.success(`${r.data.detected} áreas detectadas y agregadas al plano`);
            await loadPlans();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo detectar áreas. ¿Este plano fue creado desde un bosquejo?");
        } finally {
            setDetectingAreas(false);
        }
    };


    const autoCropPlan = async () => {
        if (!activePlan) return;
        try {
            const r = await api.post(`/floor-plans/${activePlan.id}/crop`, { x: 0, y: 0, w: 0, h: 0, auto: true });
            toast.success("Plano recortado automáticamente");
            await loadPlans();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error al recortar"); }
    };

    const startManualCrop = () => {
        setManualCropMode(true);
        setManualCropBox(null);
    };

    const handleCropPointerDown = (e) => {
        const el = cropImgRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const startX = ((e.clientX - rect.left) / rect.width) * 100;
        const startY = ((e.clientY - rect.top) / rect.height) * 100;
        const onMove = (ev) => {
            const curX = ((ev.clientX - rect.left) / rect.width) * 100;
            const curY = ((ev.clientY - rect.top) / rect.height) * 100;
            setManualCropBox({
                x: Math.max(0, Math.min(startX, curX)),
                y: Math.max(0, Math.min(startY, curY)),
                w: Math.min(100, Math.abs(curX - startX)),
                h: Math.min(100, Math.abs(curY - startY)),
            });
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    const applyManualCrop = async () => {
        if (!manualCropBox || !activePlan) return;
        try {
            // Use the crop image ref directly (same image the user sees)
            const imgEl = cropImgRef.current;
            let natW, natH;
            if (imgEl && imgEl.naturalWidth) {
                natW = imgEl.naturalWidth;
                natH = imgEl.naturalHeight;
            } else {
                // Fallback: load via fetch
                const url = assetUrl(activePlan.image_url);
                const resp = await fetch(url);
                const blob = await resp.blob();
                const blobUrl = URL.createObjectURL(blob);
                const img = new Image();
                await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = blobUrl; });
                URL.revokeObjectURL(blobUrl);
                natW = img.naturalWidth;
                natH = img.naturalHeight;
            }
            const cropData = {
                x: Math.round((manualCropBox.x / 100) * natW),
                y: Math.round((manualCropBox.y / 100) * natH),
                w: Math.round((manualCropBox.w / 100) * natW),
                h: Math.round((manualCropBox.h / 100) * natH),
            };
            await api.post(`/floor-plans/${activePlan.id}/crop`, cropData);
            toast.success("Plano recortado");
            setManualCropMode(false);
            setManualCropBox(null);
            await loadPlans();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al recortar");
        }
    };


    const executeCrop = async () => {
        if (!activePlan || !cropRect) return;
        try {
            await api.post(`/floor-plans/${activePlan.id}/crop`, cropRect);
            toast.success("Plano recortado");
            setCropMode(false);
            setCropRect(null);
            await loadPlans();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error al recortar"); }
    };


    // --- DEVICE PLACEMENT ---
    const handleMapClickToPlace = (x, y) => {
        if (mode !== "place-device") return;
        setPendingPlace({ x, y });
    };

    const savePendingDevice = async (form) => {
        const count = Math.max(1, Math.min(50, parseInt(form.count, 10) || 1));
        const { x: cx, y: cy } = pendingPlace;
        // Small grid spread: 3 cols × 4% spacing, clamped 2-95
        const COLS = 3, SPACING = 4;
        try {
            let lastNumber = null;
            for (let i = 0; i < count; i++) {
                const col = i % COLS;
                const row = Math.floor(i / COLS);
                const px = Math.min(95, Math.max(2, cx + col * SPACING));
                const py = Math.min(95, Math.max(2, cy + row * SPACING));
                const payload = {
                    client_id: clientId,
                    floor_plan_id: activePlan.id,
                    device_type: form.device_type,
                    zone: form.zone,
                    trap_subtype: SUBTYPES_FOR(form.device_type) ? (form.trap_subtype || "") : "",
                    area_name: form.area_name || "",
                    position_x: px,
                    position_y: py,
                };
                const r = await api.post("/pest-devices", payload);
                lastNumber = r.data.device_number;
            }
            toast.success(count === 1 ? `Creado: ${lastNumber}` : `${count} dispositivos creados (hasta ${lastNumber})`);
            setPendingPlace(null);
            setMode("view");
            loadDevices();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error al crear"); }
    };

    const moveDevice = async (deviceId, x, y) => {
        try {
            const newArea = detectAreaAtPoint(activePlan?.areas || [], x, y);
            await api.put(`/pest-devices/${deviceId}`, { position_x: x, position_y: y, area_name: newArea });
            setDevices((ds) => ds.map((d) => d.id === deviceId ? { ...d, position_x: x, position_y: y, area_name: newArea } : d));
        } catch (e) { toast.error("No se pudo mover"); }
    };

    const deleteDevice = async (d) => {
        if (!window.confirm(`Eliminar ${d.device_number}?`)) return;
        await api.delete(`/pest-devices/${d.id}`);
        toast.success("Eliminado");
        setEditingDevice(null);
        loadDevices();
    };

    const rotateDevice = async (d) => {
        if (d.device_type !== "trampa") return;
        try {
            const r = await api.post(`/pest-devices/${d.id}/rotate`);
            const deg = r.data.rotation ?? 0;
            const arrows = { 0: "▲", 90: "▶", 180: "▼", 270: "◀" };
            setDevices((ds) => ds.map((dev) => dev.id === d.id ? { ...dev, rotation: deg, rotated: deg === 180 } : dev));
            toast.success(`${d.device_number} → ${arrows[deg]} ${deg}°`, { duration: 1500 });
        } catch { toast.error("Error al rotar"); }
    };

    const saveDeviceEdit = async (form) => {
        try {
            const payload = {
                qr_code: form.qr_code,
                device_type: form.device_type,
                zone: form.zone,
                trap_subtype: SUBTYPES_FOR(form.device_type) ? (form.trap_subtype || "") : "",
                area_name: form.area_name,
                status: form.status,
            };
            await api.put(`/pest-devices/${editingDevice.id}`, payload);
            toast.success("Actualizado");
            setEditingDevice(null);
            loadDevices();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    // --- AREA PLACEMENT ---
    // State for multi-rect area creation
    const [pendingAreaName, setPendingAreaName] = useState(null); // when set, we're adding rects to this area
    const [pendingAreaRects, setPendingAreaRects] = useState([]); // accumulated rects

    const handleAreaDrawn = async ({ x, y, w, h }) => {
        if (pendingAreaName) {
            // Adding additional rect to existing pending area
            setPendingAreaRects(prev => [...prev, { x, y, w, h }]);
            toast.success(`Rectángulo ${pendingAreaRects.length + 2} agregado a "${pendingAreaName}". Dibuja otro o haz clic en "Guardar área".`);
            return;
        }
        const name = window.prompt("Nombre del área (ej. Cocina, Bodega Norte):");
        if (!name) return;
        // First rect — enter multi-rect mode
        setPendingAreaName(name);
        setPendingAreaRects([{ x, y, w, h }]);
        toast.info(`"${name}" — primer rectángulo. Dibuja otro para forma en L, o haz clic en "Guardar área".`, { duration: 6000 });
    };

    const savePendingArea = async () => {
        if (!pendingAreaName || pendingAreaRects.length === 0 || !activePlan) return;
        const primary = pendingAreaRects[0];
        const additional = pendingAreaRects.slice(1);
        try {
            const r = await api.post(`/floor-plans/${activePlan.id}/areas`, {
                name: pendingAreaName,
                x: primary.x, y: primary.y, w: primary.w, h: primary.h,
                rects: additional,
            });
            setActivePlan(r.data);
            setPlans((ps) => ps.map((p) => p.id === r.data.id ? r.data : p));
            toast.success(`Área "${pendingAreaName}" guardada con ${pendingAreaRects.length} rectángulo(s)`);
            setPendingAreaName(null);
            setPendingAreaRects([]);
            setMode("view");
        } catch (e) { toast.error("Error al guardar área"); }
    };

    const cancelPendingArea = () => {
        setPendingAreaName(null);
        setPendingAreaRects([]);
        setMode("view");
    };

    const deleteArea = async (areaId) => {
        if (!window.confirm("Eliminar esta área?")) return;
        const r = await api.delete(`/floor-plans/${activePlan.id}/areas/${areaId}`);
        setActivePlan(r.data);
        setPlans((ps) => ps.map((p) => p.id === r.data.id ? r.data : p));
        toast.success("Área eliminada");
    };

    // --- CSV IMPORT ---
    const uploadCsv = async (file) => {
        if (!file || !activePlan) return;
        const fd = new FormData();
        fd.append("client_id", clientId);
        fd.append("floor_plan_id", activePlan.id);
        fd.append("file", file);
        try {
            const r = await api.post("/pest-devices/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const ok = r.data.created;
            const errs = r.data.errors?.length || 0;
            toast.success(`Importados: ${ok}${errs ? `. Errores: ${errs}` : ""}`);
            if (errs) {
                console.warn("CSV errores:", r.data.errors);
                alert("Errores:\n" + r.data.errors.map(e => `Línea ${e.line}: ${e.error}`).join("\n"));
            }
            loadDevices();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const printPlan = () => {
        if (!activePlan) { toast.error("Selecciona un plano primero"); return; }

        const clientName = (clients.find((c) => c.id === clientId) || {}).name || "";
        const branchName = selectedBranch?.name || "";
        const planName = activePlan.name || branchName || "Plano";
        const dateStr = new Date().toLocaleDateString("es-SV", { year: "numeric", month: "long", day: "numeric" });
        const imgSrc = assetUrl(activePlan.image_url);
        const isPdf = (activePlan.image_url || "").toLowerCase().endsWith(".pdf");

        if (isPdf) {
            window.open(imgSrc, "_blank");
            toast.info("Este plano es PDF; se abrió para imprimir (sin superponer dispositivos).");
            return;
        }

        const ZONE_COLOR = { verde: "#16a34a", amarilla: "#eab308", roja: "#dc2626" };
        const UV_COLOR = "#9333ea";
        const SUBTYPE_COLOR = {
            caja: "#1a1a1a", tubo: "#1e40af", roca: "#92400e",
            gato_papel: "#dc2626", bandeja: "#f97316", multicaptura: "#16a34a", trex: "#0891b2", trampa_paso: "#db2777",
        };
        const TYPE_ICON = { cebadero_cebo: "C", trampa: "T", lampara_uv: "U" };

        const markerSvg = (d) => {
            const baseColor = d.device_type === "lampara_uv"
                ? UV_COLOR
                : (d.trap_subtype && SUBTYPE_COLOR[d.trap_subtype]) ? SUBTYPE_COLOR[d.trap_subtype] : (ZONE_COLOR[d.zone] || "#334155");
            const rot = d.device_type === "trampa" ? (d.rotation || (d.rotated ? 180 : 0)) : 0;
            let shape;
            if (d.device_type === "cebadero_cebo") {
                if (d.trap_subtype === "tubo") shape = `<rect x="1" y="7" width="26" height="14" rx="2" fill="${baseColor}" stroke="white" stroke-width="2"/>`;
                else if (d.trap_subtype === "roca") shape = `<circle cx="14" cy="14" r="12" fill="${baseColor}" stroke="white" stroke-width="2"/>`;
                else shape = `<rect x="2" y="2" width="24" height="24" rx="3" fill="${baseColor}" stroke="white" stroke-width="2"/>`;
            } else if (d.device_type === "lampara_uv") {
                shape = `<polygon points="14,1 27,14 14,27 1,14" fill="${baseColor}" stroke="white" stroke-width="2"/>`;
            } else {
                shape = `<polygon points="14,2 26,26 2,26" fill="${baseColor}" stroke="white" stroke-width="2"/>`;
            }
            const ty = d.device_type === "trampa" ? 20 : 16;
            return `<svg width="24" height="24" viewBox="0 0 28 28" style="transform:rotate(${rot}deg);filter:drop-shadow(0 1px 1px rgba(0,0,0,0.3))">
                ${shape}
                <text x="14" y="${ty}" text-anchor="middle" fill="white" font-size="10" font-weight="bold" style="transform:rotate(${-rot}deg);transform-origin:14px 14px">${TYPE_ICON[d.device_type] || "?"}</text>
            </svg>`;
        };

        const areasHtml = (activePlan.areas || []).map((a) => {
            const rects = [{ x: a.x, y: a.y, w: a.w, h: a.h }, ...(a.rects || [])];
            return rects.map((r, ri) => `
                <div class="area" style="left:${r.x}%;top:${r.y}%;width:${r.w}%;height:${r.h}%">
                    ${ri === 0 ? `<div class="lbl"><span>${(a.name || "").replace(/</g, "&lt;")}</span></div>` : ""}
                </div>`).join("");
        }).join("");

        const markersHtml = (devices || []).map((d) => `
            <div class="marker" style="left:${d.position_x}%;top:${d.position_y}%">
                ${markerSvg(d)}
                <div class="num">${(d.device_number || "").replace(/</g, "&lt;")}</div>
            </div>`).join("");

        const deviceColor = (d) => d.device_type === "lampara_uv"
            ? UV_COLOR
            : (d.trap_subtype && SUBTYPE_COLOR[d.trap_subtype]) ? SUBTYPE_COLOR[d.trap_subtype] : (ZONE_COLOR[d.zone] || "#334155");
        const deviceLabel = (d) => (d.trap_subtype && ALL_SUBTYPE_LABELS[d.trap_subtype]) || TYPES[d.device_type] || "Dispositivo";
        const sortedDevices = (devices || []).slice().sort((a, b) =>
            (a.device_number || "").localeCompare(b.device_number || "", undefined, { numeric: true, sensitivity: "base" }));
        const deviceListHtml = sortedDevices.map((d) => `
            <div class="dev-item">
                <span class="dev-dot" style="background:${deviceColor(d)}"></span>
                <span class="dev-num">${(d.device_number || "—").replace(/</g, "&lt;")}</span>
                <span class="dev-sep">-</span>
                <span class="dev-type">${deviceLabel(d).replace(/</g, "&lt;")}</span>
            </div>`).join("");

        const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>${planName}</title>
        <style>
            @page { size: A4 landscape; margin: 7mm; }
            * { box-sizing: border-box; }
            html, body { height: auto; }
            body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #111; }
            .hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #CC0000; padding-bottom: 5px; margin-bottom: 8px; }
            .hdr h1 { font-size: 15px; margin: 0; color: #0B1F3A; }
            .hdr .sub { font-size: 10px; color: #555; margin-top: 2px; }
            .hdr .date { font-size: 10px; color: #888; white-space: nowrap; }
            .content { display: flex; gap: 6mm; align-items: flex-start; }
            .sidebar { flex: 0 0 50mm; width: 50mm; }
            .sidebar h2 { font-size: 10px; color: #0B1F3A; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #d9d9d9; padding-bottom: 3px; margin: 0 0 5px 0; }
            .dev-list { font-size: 8pt; }
            .dev-item { display: flex; align-items: center; gap: 4px; padding: 1.2px 0; break-inside: avoid; }
            .dev-dot { width: 8px; height: 8px; border-radius: 2px; flex: 0 0 8px; border: 1px solid rgba(0,0,0,0.2); }
            .dev-num { font-family: 'Courier New', monospace; font-weight: bold; color: #111; min-width: 36px; }
            .dev-sep { color: #9ca3af; }
            .dev-type { color: #374151; }
            .map-area { flex: 1; min-width: 0; text-align: center; }
            .map { position: relative; display: inline-block; line-height: 0; page-break-inside: avoid; break-inside: avoid; }
            .map > img { display: block; width: auto; height: auto; max-width: 100%; max-height: 168mm; }
            .area { position: absolute; background: rgba(37,99,235,0.12); border: 1px solid rgba(37,99,235,0.55); }
            .area .lbl { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; line-height: normal; }
            .area .lbl span { background: #2563eb; color: #fff; font-size: 9px; padding: 1px 4px; border-radius: 3px; white-space: nowrap; opacity: 0.85; }
            .marker { position: absolute; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; line-height: normal; }
            .marker .num { font-size: 8px; background: rgba(0,0,0,0.78); color: #fff; padding: 0 3px; border-radius: 2px; margin-top: 1px; white-space: nowrap; }
            @media print { .no-print { display: none; } }
        </style></head><body>
            <div class="hdr">
                <div>
                    <h1>${planName.replace(/</g, "&lt;")}</h1>
                    <div class="sub">${clientName.replace(/</g, "&lt;")}${branchName ? " · " + branchName.replace(/</g, "&lt;") : ""} · ${(devices || []).length} dispositivos</div>
                </div>
                <div class="date">${dateStr}</div>
            </div>
            <div class="content">
                <div class="sidebar">
                    <h2>Dispositivos (${(devices || []).length})</h2>
                    <div class="dev-list">
                        ${deviceListHtml || '<div class="dev-type">Sin dispositivos</div>'}
                    </div>
                </div>
                <div class="map-area">
                    <div class="map">
                        <img src="${imgSrc}" alt="plano" />
                        ${areasHtml}
                        ${markersHtml}
                    </div>
                </div>
            </div>
            <script>
                const img = document.querySelector('.map > img');
                const go = () => setTimeout(() => { window.focus(); window.print(); }, 250);
                if (img.complete) go(); else { img.onload = go; img.onerror = go; }
            <\/script>
        </body></html>`;

        const w = window.open("", "_blank");
        if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
        w.document.open();
        w.document.write(html);
        w.document.close();
    };

    const openQrSheet = () => {
        const token = localStorage.getItem("trulyn_token");
        let url = `${API}/pest-devices/qr-sheet?client_id=${clientId}`;
        if (branchId) url += `&branch_id=${branchId}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => window.open(URL.createObjectURL(b), "_blank"));
    };

    const exportQrCsv = () => {
        if (!activePlan) { toast.error("Selecciona un plano primero"); return; }
        const token = localStorage.getItem("trulyn_token");
        const url = `${API}/pest-devices/qr-export.xlsx?floor_plan_id=${activePlan.id}`;
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
            .then((b) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b);
                a.download = `qr_${(activePlan.name || "plano").replace(/\s+/g, "_")}.xlsx`;
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                toast.success("Excel exportado para BarTender");
            })
            .catch(() => toast.error("No se pudo exportar el Excel"));
    };

    const counts = useMemo(() => {
        const c = { verde: 0, amarilla: 0, roja_trampa: 0, roja_uv: 0 };
        const bySubtype = {};
        for (const d of devices) {
            if (d.device_type === "lampara_uv") c.roja_uv += 1;
            else if (d.zone === "roja") c.roja_trampa += 1;
            else c[d.zone] = (c[d.zone] || 0) + 1;
            if (d.trap_subtype) {
                bySubtype[d.trap_subtype] = (bySubtype[d.trap_subtype] || 0) + 1;
            }
        }
        c.bySubtype = bySubtype;
        return c;
    }, [devices]);

    return (
        <div data-testid="admin-floor-plans">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                    Mapas Interactivos
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                    <FactCombobox clients={clients} value={clientId} onChange={setClientId} allBranches={allBranches} />
                    {clientId && branches.length > 0 && (
                        <BranchCombobox branches={branches} value={branchId} onChange={setBranchId} branchesWithPlans={branchesWithPlans} />
                    )}
                    <Button variant="outline" onClick={openQrSheet} disabled={!devices.length} data-testid="map-qr-sheet">
                        <QrCode className="w-4 h-4 mr-1" /> Hoja QR
                    </Button>
                    <Button variant="outline" onClick={exportQrCsv} disabled={!activePlan || !devices.length} data-testid="map-qr-csv">
                        <FileUp className="w-4 h-4 mr-1" /> Exportar QR (Excel)
                    </Button>
                    <Button variant="outline" onClick={printPlan} disabled={!activePlan} data-testid="map-print-plan">
                        <Printer className="w-4 h-4 mr-1" /> Imprimir
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
                {/* LEFT — Plans list */}
                <Card className="p-3 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm">Planos del cliente</div>
                        <div className="flex items-center gap-1">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSketchOpen(true)}
                                disabled={!clientId}
                                title="Subir bosquejo dibujado por el técnico (IA lo limpia)"
                                data-testid="map-upload-sketch"
                            >
                                <Sparkles className="w-4 h-4 text-[#FFD62B]" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => planFileRef.current?.click()} title="Subir plano PNG/JPG/PDF existente" data-testid="map-upload-plan">
                                <Upload className="w-4 h-4" />
                            </Button>
                        </div>
                        <input
                            ref={planFileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,application/pdf"
                            className="hidden"
                            onChange={(e) => { if (e.target.files?.[0]) uploadPlan(e.target.files[0]); e.target.value = ""; }}
                        />
                    </div>
                    {!plans.length && <div className="text-xs text-muted-foreground">Sin planos. Sube uno con el botón ⬆</div>}
                    {plans.map((p) => (
                        <button
                            key={p.id}
                            className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 ${activePlan?.id === p.id ? "bg-slate-100 font-semibold" : ""}`}
                            onClick={() => setActivePlan(p)}
                            data-testid={`plan-item-${p.id}`}
                        >
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {p.name}
                        </button>
                    ))}

                    {activePlan && (
                        <div className="border-t pt-2 mt-2 space-y-1">
                            <Button size="sm" variant="outline" className="w-full" onClick={autoCropPlan} data-testid="map-crop-plan">
                                <Crop className="w-3 h-3 mr-1" /> Recortar bordes blancos
                            </Button>
                            <Button size="sm" variant="outline" className="w-full" onClick={startManualCrop} data-testid="map-manual-crop">
                                <Crop className="w-3 h-3 mr-1" /> Recortar manual
                            </Button>
                            <Button size="sm" variant="outline" className="w-full" onClick={async () => {
                                try {
                                    await api.post(`/floor-plans/${activePlan.id}/add-padding`);
                                    toast.success("Margen agregado");
                                    await loadPlans();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                            }} data-testid="map-add-padding">
                                <Plus className="w-3 h-3 mr-1" /> Agregar margen exterior
                            </Button>
                            <Button size="sm" variant="outline" className="w-full" onClick={renamePlan} data-testid="map-rename-plan">
                                <Pencil className="w-3 h-3 mr-1" /> Renombrar
                            </Button>
                            <Button size="sm" variant="outline" className="w-full" onClick={detectAreasForPlan} disabled={detectingAreas} data-testid="map-detect-areas">
                                <Sparkles className="w-3 h-3 mr-1" /> {detectingAreas ? "Detectando..." : "Detectar áreas (IA)"}
                            </Button>
                            <Button size="sm" variant="outline" className="w-full text-red-600" onClick={deletePlan} data-testid="map-delete-plan">
                                <Trash2 className="w-3 h-3 mr-1" /> Eliminar plano
                            </Button>
                        </div>
                    )}

                    <div className="border-t pt-2 mt-2 text-xs space-y-1">
                        <div className="flex justify-between"><span>Total dispositivos:</span><b>{devices.length}</b></div>
                        <div className="flex justify-between" style={{color:"#1f2937"}}><span>● Cebadero caja:</span><b>{counts.bySubtype?.caja || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#6b7280"}}><span>● Cebadero tubo:</span><b>{counts.bySubtype?.tubo || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#78716c"}}><span>● Cebadero roca:</span><b>{counts.bySubtype?.roca || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#dc2626"}}><span>● Trampa gato papel:</span><b>{counts.bySubtype?.gato_papel || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#f97316"}}><span>● Trampa bandeja:</span><b>{counts.bySubtype?.bandeja || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#16a34a"}}><span>● Trampa multicaptura:</span><b>{counts.bySubtype?.multicaptura || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#0891b2"}}><span>● Trampa T-Rex:</span><b>{counts.bySubtype?.trex || 0}</b></div>
                        <div className="flex justify-between" style={{color:"#db2777"}}><span>● Trampa de paso:</span><b>{counts.bySubtype?.trampa_paso || 0}</b></div>
                        <div className="flex justify-between text-purple-700"><span>● UV (lámparas):</span><b>{counts.roja_uv}</b></div>
                    </div>

                    {activePlan?.areas?.length > 0 && (
                        <div className="border-t pt-2 mt-2 text-xs">
                            <div className="font-semibold mb-1.5">Áreas nombradas:</div>
                            <div className="flex flex-wrap gap-1.5">
                                {activePlan.areas.map((a) => (
                                    <span key={a.area_id} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-300 px-2 py-0.5 rounded">
                                        {a.name}
                                        <button onClick={() => deleteArea(a.area_id)} className="text-red-600 hover:text-red-800" data-testid={`area-delete-${a.area_id}`}>
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Pending device requests */}
                    {deviceRequests.length > 0 && (
                        <div className="border-t pt-2 mt-2 text-xs" data-testid="device-requests-panel">
                            <div className="font-semibold mb-1.5 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
                                Solicitudes de técnicos ({deviceRequests.length})
                            </div>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                {deviceRequests.map((req) => {
                                    const typeLabel = req.device_type === "cebadero_cebo" ? "Cebadero" : req.device_type === "trampa" ? "Trampa" : "UV";
                                    const subLabel = req.trap_subtype || "";
                                    return (
                                        <div key={req.id} className="bg-orange-50 border border-orange-200 rounded p-2 space-y-1">
                                            <div className="flex justify-between items-start">
                                                <span className="font-medium">{typeLabel}{subLabel ? ` — ${subLabel}` : ""}</span>
                                                <span className="text-[10px] text-muted-foreground">{req.zone}</span>
                                            </div>
                                            {req.comment && <div className="text-muted-foreground italic">"{req.comment}"</div>}
                                            <div className="text-[10px] text-muted-foreground">Por: {req.requested_by} · {new Date(req.created_at).toLocaleDateString("es-SV")}</div>
                                            <div className="flex gap-1.5 pt-1">
                                                <Button size="sm" variant="outline" className="h-6 text-[10px] text-green-700 border-green-300 hover:bg-green-50 flex-1"
                                                    onClick={async () => {
                                                        await api.put(`/device-requests/${req.id}`, { status: "approved" });
                                                        setDeviceRequests(rs => rs.filter(r => r.id !== req.id));
                                                        toast.success("Solicitud aprobada — agrega el dispositivo manualmente");
                                                    }}
                                                >Aprobar</Button>
                                                <Button size="sm" variant="outline" className="h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50 flex-1"
                                                    onClick={async () => {
                                                        await api.put(`/device-requests/${req.id}`, { status: "rejected" });
                                                        setDeviceRequests(rs => rs.filter(r => r.id !== req.id));
                                                        toast.success("Solicitud rechazada");
                                                    }}
                                                >Rechazar</Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                </Card>

                {/* RIGHT — Canvas */}
                <Card className="p-3">
                    {!activePlan && (
                        <div className="py-16 text-center text-muted-foreground">
                            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-40" />
                            <div>Sube un plano para comenzar.</div>
                            <div className="flex flex-wrap gap-2 justify-center mt-3">
                                <Button onClick={() => planFileRef.current?.click()}>
                                    <Upload className="w-4 h-4 mr-1" /> Subir plano (PNG/JPG/PDF)
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setSketchOpen(true)}
                                    disabled={!clientId}
                                    className="border-[#FFD62B] text-[#0A1A33]"
                                    data-testid="empty-upload-sketch"
                                >
                                    <Sparkles className="w-4 h-4 mr-1 text-[#FFD62B]" /> Subir bosquejo del técnico (IA)
                                </Button>
                            </div>
                        </div>
                    )}

                    {activePlan && (
                        <>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <Button size="sm" variant={mode === "view" ? "default" : "outline"} onClick={() => setMode("view")} data-testid="map-mode-view">
                                    <MousePointer2 className="w-3.5 h-3.5 mr-1" /> Ver
                                </Button>
                                <Button size="sm" variant={mode === "place-device" ? "default" : "outline"} onClick={() => setMode("place-device")} data-testid="map-mode-place">
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Colocar dispositivo
                                </Button>
                                <Button size="sm" variant={mode === "move-devices" ? "default" : "outline"} onClick={() => setMode("move-devices")} data-testid="map-mode-move">
                                    <Move className="w-3.5 h-3.5 mr-1" /> Mover
                                </Button>
                                <Button size="sm" variant={mode === "edit-devices" ? "default" : "outline"} onClick={() => setMode("edit-devices")} data-testid="map-mode-edit">
                                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                                </Button>
                                <Button size="sm" variant={mode === "place-area" ? "default" : "outline"} onClick={() => setMode("place-area")} data-testid="map-mode-area">
                                    <Square className="w-3.5 h-3.5 mr-1" /> Nombrar área
                                </Button>
                                <Button
                                    size="sm"
                                    variant={mode === "edit-area" ? "default" : "outline"}
                                    onClick={() => {
                                        // Auto-save pending area if exists
                                        if (pendingAreaName && pendingAreaRects.length > 0) savePendingArea();
                                        setMode("edit-area"); setEditingAreaDraft(null); setEditingAreas([...(activePlan?.areas || [])]); setAreasModified(false);
                                    }}
                                    data-testid="map-mode-edit-area"
                                >
                                    <Move className="w-3.5 h-3.5 mr-1" /> Editar área nombrada
                                </Button>
                                <Button size="sm" variant={mode === "erase" ? "default" : "outline"} onClick={() => setMode("erase")} data-testid="map-mode-erase">
                                    <Eraser className="w-3.5 h-3.5 mr-1" /> Borrador
                                </Button>
                                <span className="border-l pl-2 ml-2 flex gap-2">
                                    <Button size="sm" variant="outline" onClick={undoMap} disabled={!canUndo} data-testid="map-undo" title="Deshacer">
                                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Deshacer
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={redoMap} disabled={!canRedo} data-testid="map-redo" title="Rehacer">
                                        <Redo2 className="w-3.5 h-3.5 mr-1" /> Rehacer
                                    </Button>
                                    {/* Importar CSV oculto (no se usa actualmente); reemplazado por Deshacer/Rehacer.
                                    <Button size="sm" variant="outline" onClick={() => csvInputRef.current?.click()} data-testid="map-import-csv">
                                        <FileUp className="w-3.5 h-3.5 mr-1" /> Importar CSV
                                    </Button>
                                    <input
                                        ref={csvInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="hidden"
                                        onChange={(e) => { if (e.target.files?.[0]) uploadCsv(e.target.files[0]); e.target.value = ""; }}
                                    />
                                    */}
                                </span>
                            </div>

                            {mode === "place-device" && (
                                <div className="text-xs bg-yellow-50 border border-yellow-300 text-yellow-900 rounded p-2 mb-2">
                                    Clic sobre el plano para colocar dispositivo(s) en esa posición.
                                </div>
                            )}
                            {mode === "move-devices" && (
                                <div className="text-xs bg-blue-50 border border-blue-300 text-blue-900 rounded p-2 mb-2">
                                    Arrastra los marcadores para reposicionarlos. (No abre edición.)
                                </div>
                            )}
                            {mode === "edit-devices" && (
                                <div className="text-xs bg-orange-50 border border-orange-300 text-orange-900 rounded p-2 mb-2">
                                    Clic sobre un marcador para editar. Doble clic en trampas (▲) para rotar 90°.
                                </div>
                            )}
                            {mode === "place-area" && (
                                <div className="bg-blue-50 border border-blue-300 text-blue-900 rounded p-2 mb-2 space-y-2">
                                    {pendingAreaName ? (
                                        <>
                                            <div className="text-xs font-semibold">"{pendingAreaName}" — {pendingAreaRects.length} rectángulo(s)</div>
                                            <div className="text-xs">Dibuja otro rectángulo para formar la L, o guarda el área.</div>
                                            {/* Show pending rects as preview */}
                                            <div className="flex gap-2 pt-1">
                                                <Button size="sm" variant="ghost" onClick={cancelPendingArea}>Cancelar</Button>
                                                <Button size="sm" onClick={savePendingArea} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-pending-area">
                                                    Guardar área ({pendingAreaRects.length} rect{pendingAreaRects.length > 1 ? "s" : ""})
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-xs">Mantén presionado y arrastra para dibujar el área. Suelta para nombrarla.</div>
                                    )}
                                </div>
                            )}
                            {mode === "edit-area" && (
                                <div className="bg-purple-50 border border-purple-300 rounded p-2 mb-2 space-y-2" data-testid="edit-area-toolbar">
                                    <div className="text-xs text-purple-900 font-semibold">Editando áreas — haz clic en una para seleccionarla</div>
                                    {editingAreas.map((a, i) => (
                                        <div key={a.area_id} className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs ${editingAreaDraft?.area_id === a.area_id ? "bg-purple-200 ring-1 ring-purple-400" : "bg-white"}`}>
                                            <Input
                                                value={a.name}
                                                onChange={(e) => {
                                                    const updated = [...editingAreas];
                                                    updated[i] = { ...updated[i], name: e.target.value };
                                                    setEditingAreas(updated);
                                                    setAreasModified(true);
                                                    if (editingAreaDraft?.area_id === a.area_id) {
                                                        setEditingAreaDraft({ ...editingAreaDraft, name: e.target.value });
                                                    }
                                                }}
                                                className="h-6 text-xs flex-1"
                                                onClick={() => setEditingAreaDraft(a)}
                                            />
                                            <Button
                                                size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                                                onClick={() => {
                                                    if (!window.confirm(`¿Eliminar "${a.name}"?`)) return;
                                                    setEditingAreas(editingAreas.filter(ea => ea.area_id !== a.area_id));
                                                    setAreasModified(true);
                                                    if (editingAreaDraft?.area_id === a.area_id) setEditingAreaDraft(null);
                                                }}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                    {editingAreas.length === 0 && <div className="text-xs text-muted-foreground text-center py-1">Sin áreas. Usa "Nombrar área" para agregar.</div>}
                                    <div className="flex gap-2 pt-1">
                                        <Button size="sm" variant="ghost" onClick={() => { setMode("view"); setEditingAreaDraft(null); setEditingAreas([]); setAreasModified(false); }}>
                                            Cancelar
                                        </Button>
                                        <Button
                                            size="sm"
                                            disabled={!areasModified}
                                            onClick={async () => {
                                                const invalid = editingAreas.find(a => !a.name.trim());
                                                if (invalid) { toast.error("Todas las áreas deben tener nombre"); return; }
                                                try {
                                                    await api.put(`/floor-plans/${activePlan.id}/areas-batch`, editingAreas);
                                                    toast.success("Áreas guardadas");
                                                    setAreasModified(false);
                                                    setEditingAreaDraft(null);
                                                    await loadPlans();
                                                } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
                                            }}
                                            className="bg-purple-600 hover:bg-purple-700 text-white"
                                            data-testid="edit-areas-save-all"
                                        >
                                            <Save className="w-3.5 h-3.5 mr-1" /> Guardar todo ({editingAreas.length})
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {manualCropMode && activePlan && !activePlan.image_url?.toLowerCase().endsWith(".pdf") ? (
                                <ManualCropView
                                    imageUrl={assetUrl(activePlan.image_url)}
                                    onApply={applyManualCrop}
                                    onCancel={() => { setManualCropMode(false); setManualCropBox(null); }}
                                    cropBox={manualCropBox}
                                    setCropBox={setManualCropBox}
                                    cropImgRef={cropImgRef}
                                />
                            ) : mode === "erase" && activePlan && !activePlan.image_url?.toLowerCase().endsWith(".pdf") ? (
                                <EraserView
                                    imageUrl={assetUrl(activePlan.image_url)}
                                    eraserSize={eraserSize}
                                    setEraserSize={setEraserSize}
                                    canvasRef={eraserCanvasRef}
                                    onSave={async (dataUrl) => {
                                        try {
                                            await api.post(`/floor-plans/${activePlan.id}/replace-image`, { image_base64: dataUrl }, { timeout: 120000 });
                                            toast.success("Plano guardado");
                                            setMode("view");
                                            await loadPlans();
                                        } catch (e) { toast.error("Error al guardar"); }
                                    }}
                                    onCancel={() => setMode("view")}
                                />
                            ) : (
                            <FloorPlanCanvas
                                imageUrl={activePlan.image_url}
                                devices={devices}
                                areas={[
                                    ...(mode === "edit-area"
                                        ? (editingAreaDraft
                                            ? editingAreas.filter((a) => a.area_id !== editingAreaDraft.area_id)
                                            : editingAreas)
                                        : (activePlan.areas || [])),
                                    ...(pendingAreaName && pendingAreaRects.length > 0
                                        ? [{ area_id: "__pending__", name: pendingAreaName, x: pendingAreaRects[0].x, y: pendingAreaRects[0].y, w: pendingAreaRects[0].w, h: pendingAreaRects[0].h, rects: pendingAreaRects.slice(1) }]
                                        : []),
                                ]}
                                mode={mode}
                                draggable={mode === "move-devices"}
                                areaStatusByAreaId={areaStatusByAreaId}
                                onMapClick={handleMapClickToPlace}
                                onAreaDrawn={handleAreaDrawn}
                                onDeviceMove={moveDevice}
                                onDeviceClick={(d) => { if (mode === "edit-devices") setEditingDevice(d); }}
                                onDeviceDoubleClick={(d) => { if (mode === "edit-devices" || mode === "view") rotateDevice(d); }}
                                onAreaClick={(a) => {
                                    if (mode === "edit-area") {
                                        const local = editingAreas.find(ea => ea.area_id === a.area_id);
                                        if (local) setEditingAreaDraft({ ...local });
                                    } else {
                                        setEditingArea(a);
                                    }
                                }}
                                draftArea={mode === "edit-area" && editingAreaDraft
                                    ? { x: editingAreaDraft.x, y: editingAreaDraft.y, w: editingAreaDraft.w, h: editingAreaDraft.h, label: editingAreaDraft.name }
                                    : null}
                                onDraftAreaChange={(d) => {
                                    if (mode === "edit-area" && editingAreaDraft) {
                                        const updated = { ...editingAreaDraft, x: d.x, y: d.y, w: d.w, h: d.h };
                                        setEditingAreaDraft(updated);
                                        setEditingAreas(prev => prev.map(a => a.area_id === updated.area_id ? updated : a));
                                        setAreasModified(true);
                                    }
                                }}
                                className="border rounded overflow-hidden"
                            />
                            )}
                        </>
                    )}
                </Card>
            </div>

            {/* Place Device Dialog */}
            <PlaceDeviceDialog
                key={pendingPlace ? `pd-${pendingPlace.x}-${pendingPlace.y}` : "pd-closed"}
                open={!!pendingPlace}
                onClose={() => { setPendingPlace(null); setMode("view"); }}
                onSave={savePendingDevice}
                areaNames={activePlan?.areas?.map((a) => a.name) || []}
                areas={activePlan?.areas || []}
                clickPoint={pendingPlace}
            />

            {/* Edit Device Dialog */}
            <EditDeviceDialog
                key={editingDevice ? `ed-${editingDevice.id}` : "ed-closed"}
                device={editingDevice}
                onClose={() => setEditingDevice(null)}
                onSave={saveDeviceEdit}
                onDelete={() => deleteDevice(editingDevice)}
                areaNames={activePlan?.areas?.map((a) => a.name) || []}
                areas={activePlan?.areas || []}
            />

            {/* Area details (edit + history) */}
            <AreaHistoryDialog
                open={!!editingArea}
                area={editingArea}
                planId={activePlan?.id}
                findings={areaFindings}
                onClose={() => setEditingArea(null)}
                onUpdated={async () => {
                    await loadPlans();
                }}
            />

            {/* Sketch upload (AI-driven) */}
            <SketchUploadDialog
                open={sketchOpen}
                clientId={clientId}
                branchId={branchId}
                clientName={(clients.find((c) => c.id === clientId) || {}).name}
                defaultPlanName={defaultPlanName}
                onClose={() => setSketchOpen(false)}
                onSuccess={async (floorPlanId) => {
                    await loadPlans();
                    const newPlan = (await api.get(`/floor-plans?client_id=${clientId}`)).data.find((p) => p.id === floorPlanId);
                    if (newPlan) setActivePlan(newPlan);
                }}
            />
        </div>
    );
}

// ---------- Dialogs ----------

/**
 * AreaSelector — Shadcn-styled Select for area names with "Otra..." fallback to free text.
 */
function AreaSelector({ value, onChange, areaNames, testId }) {
    const isCustom = !!value && !areaNames.includes(value);
    const [mode, setMode] = useState(isCustom ? "__custom__" : (value || "__none__"));

    const handleSelect = (v) => {
        setMode(v);
        if (v === "__none__") onChange("");
        else if (v === "__custom__") onChange("");
        else onChange(v);
    };

    return (
        <div className="space-y-2">
            <Select value={mode} onValueChange={handleSelect}>
                <SelectTrigger data-testid={testId}><SelectValue placeholder="Seleccionar área..." /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="__none__"><span className="text-muted-foreground">— Sin asignar —</span></SelectItem>
                    {areaNames.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                    <SelectItem value="__custom__">Otra ubicación...</SelectItem>
                </SelectContent>
            </Select>
            {mode === "__custom__" && (
                <Input
                    autoFocus
                    placeholder="Escribe el nombre de la ubicación"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`${testId}-custom`}
                />
            )}
        </div>
    );
}

function EraserView({ imageUrl, eraserSize, setEraserSize, canvasRef, onSave, onCancel }) {
    const imgRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [drawing, setDrawing] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load image onto canvas via fetch to avoid CORS issues with crossOrigin
    useEffect(() => {
        if (!imageUrl) return;
        const loadImage = async () => {
            try {
                const resp = await fetch(imageUrl);
                const blob = await resp.blob();
                const blobUrl = URL.createObjectURL(blob);
                const img = new Image();
                img.onload = () => {
                    const cvs = canvasRef.current;
                    if (!cvs) return;
                    cvs.width = img.naturalWidth;
                    cvs.height = img.naturalHeight;
                    const ctx = cvs.getContext("2d");
                    ctx.drawImage(img, 0, 0);
                    URL.revokeObjectURL(blobUrl);
                    setReady(true);
                };
                img.src = blobUrl;
            } catch (e) {
                console.error("Error loading image for eraser:", e);
            }
        };
        loadImage();
    }, [imageUrl, canvasRef]);

    const getCanvasXY = (e) => {
        const cvs = canvasRef.current;
        if (!cvs) return null;
        const rect = cvs.getBoundingClientRect();
        const scaleX = cvs.width / rect.width;
        const scaleY = cvs.height / rect.height;
        return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const eraseAt = (x, y) => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        const r = (eraserSize / 100) * canvasRef.current.width * 0.5;
        ctx.save();
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = "white";
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        setDirty(true);
    };

    const onPointerDown = (e) => {
        setDrawing(true);
        const pt = getCanvasXY(e);
        if (pt) eraseAt(pt.x, pt.y);
    };
    const onPointerMove = (e) => {
        if (!drawing) return;
        const pt = getCanvasXY(e);
        if (pt) eraseAt(pt.x, pt.y);
    };
    const onPointerUp = () => setDrawing(false);

    const handleSave = async () => {
        const cvs = canvasRef.current;
        if (!cvs) return;
        setSaving(true);
        // Use JPEG with quality to reduce payload size (avoids Cloudflare limits)
        const dataUrl = cvs.toDataURL("image/jpeg", 0.92);
        await onSave(dataUrl);
        setSaving(false);
    };

    return (
        <div className="flex flex-col h-full">
            <div className="sticky top-0 z-10 bg-white border-b px-3 py-2 flex items-center gap-3 shadow-sm rounded-t">
                <Eraser className="w-4 h-4 text-slate-600" />
                <span className="text-xs text-slate-700 font-medium">Borrador — pinta de blanco sobre el plano</span>
                <div className="flex items-center gap-1.5 ml-2">
                    <label className="text-xs text-muted-foreground">Grosor:</label>
                    <input
                        type="range" min="2" max="60" value={eraserSize}
                        onChange={(e) => setEraserSize(Number(e.target.value))}
                        className="w-24 h-1.5 accent-slate-700"
                        data-testid="eraser-size-slider"
                    />
                    <span className="text-xs font-mono w-6 text-center">{eraserSize}</span>
                </div>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4"
                    data-testid="eraser-save-btn"
                >
                    {saving ? "Guardando..." : <><Save className="w-4 h-4 mr-1" /> Guardar plano</>}
                </Button>
            </div>
            <div className="overflow-auto flex-1 bg-slate-100 flex items-start justify-center" style={{ maxHeight: "calc(100vh - 180px)" }}>
                <canvas
                    ref={canvasRef}
                    className="max-w-full rounded"
                    style={{ cursor: "crosshair", touchAction: "none" }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerLeave={onPointerUp}
                />
            </div>
        </div>
    );
}


function ManualCropView({ imageUrl, onApply, onCancel, cropBox, setCropBox, cropImgRef, onPointerDown }) {
    const [zoom, setZoom] = useState(100);
    const containerRef = useRef(null);

    const zoomIn = () => setZoom((z) => Math.min(300, z + 25));
    const zoomOut = () => setZoom((z) => Math.max(10, z - (z > 50 ? 25 : 10)));
    const zoomReset = () => setZoom(100);

    // Recalculate crop pointer events relative to the scaled image, with edge auto-scroll
    const handleScaledPointerDown = (e) => {
        const el = cropImgRef.current;
        const container = containerRef.current;
        if (!el || !container) return;
        const imgRect = el.getBoundingClientRect();
        const startX = ((e.clientX - imgRect.left) / imgRect.width) * 100;
        const startY = ((e.clientY - imgRect.top) / imgRect.height) * 100;
        let scrollRAF = null;

        const onMove = (ev) => {
            const currentImgRect = el.getBoundingClientRect();
            const curX = Math.max(0, Math.min(100, ((ev.clientX - currentImgRect.left) / currentImgRect.width) * 100));
            const curY = Math.max(0, Math.min(100, ((ev.clientY - currentImgRect.top) / currentImgRect.height) * 100));
            setCropBox({
                x: Math.max(0, Math.min(startX, curX)),
                y: Math.max(0, Math.min(startY, curY)),
                w: Math.min(100, Math.abs(curX - startX)),
                h: Math.min(100, Math.abs(curY - startY)),
            });
            // Auto-scroll when cursor nears container edges
            const cRect = container.getBoundingClientRect();
            const edgeZone = 40;
            let dx = 0, dy = 0;
            if (ev.clientY > cRect.bottom - edgeZone) dy = 12;
            else if (ev.clientY < cRect.top + edgeZone) dy = -12;
            if (ev.clientX > cRect.right - edgeZone) dx = 12;
            else if (ev.clientX < cRect.left + edgeZone) dx = -12;
            if (dx || dy) {
                if (!scrollRAF) {
                    const doScroll = () => {
                        container.scrollBy(dx, dy);
                        scrollRAF = requestAnimationFrame(doScroll);
                    };
                    scrollRAF = requestAnimationFrame(doScroll);
                }
            } else if (scrollRAF) {
                cancelAnimationFrame(scrollRAF);
                scrollRAF = null;
            }
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            if (scrollRAF) cancelAnimationFrame(scrollRAF);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Sticky toolbar at top */}
            <div className="sticky top-0 z-10 bg-white border-b px-3 py-2 flex items-center gap-2 shadow-sm rounded-t">
                <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-300 rounded px-2 py-1 flex-1">
                    Arrastra sobre la imagen para seleccionar el área a conservar
                </div>
                <div className="flex items-center gap-1 border rounded px-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={zoomOut} data-testid="crop-zoom-out">−</Button>
                    <button className="text-xs font-mono w-10 text-center" onClick={zoomReset}>{zoom}%</button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={zoomIn} data-testid="crop-zoom-in">+</Button>
                </div>
                <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
                <Button
                    size="sm"
                    onClick={onApply}
                    disabled={!cropBox}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4"
                    data-testid="apply-manual-crop"
                >
                    <Crop className="w-4 h-4 mr-1" /> Aplicar recorte
                </Button>
            </div>
            {/* Scrollable + zoomable image area */}
            <div ref={containerRef} className="overflow-auto flex-1 bg-slate-100" style={{ maxHeight: "calc(100vh - 180px)" }}>
                <div className="relative inline-block select-none" style={{ width: `${zoom}%`, cursor: "crosshair" }} onPointerDown={handleScaledPointerDown}>
                    <img
                        ref={cropImgRef}
                        src={imageUrl}
                        alt="crop"
                        className="w-full rounded"
                        draggable={false}
                    />
                    {cropBox && (
                        <div className="absolute inset-0 pointer-events-none">
                            <div style={{
                                position: "absolute",
                                left: `${cropBox.x}%`, top: `${cropBox.y}%`,
                                width: `${cropBox.w}%`, height: `${cropBox.h}%`,
                                boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
                                border: "2px dashed #FFD62B",
                            }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PlaceDeviceDialog({ open, onClose, onSave, areaNames, areas, clickPoint }) {
    const detectedArea = (clickPoint && areas) ? detectAreaAtPoint(areas, clickPoint.x, clickPoint.y) : "";
    const [form, setForm] = useState({ device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", area_name: detectedArea, count: 1 });
    const allowedTypes = ZONES.find((z) => z.v === form.zone)?.allow || [];

    const setZone = (zone) => {
        const allow = ZONES.find((z) => z.v === zone)?.allow || [];
        const nextType = allow.includes(form.device_type) ? form.device_type : allow[0];
        setForm((s) => ({ ...s, zone, device_type: nextType, trap_subtype: DEFAULT_SUBTYPE_FOR(nextType) }));
    };

    const setDeviceType = (v) => {
        setForm((s) => ({ ...s, device_type: v, trap_subtype: DEFAULT_SUBTYPE_FOR(v) }));
    };

    // Compute the prefix preview for the next auto-generated QR
    const previewPrefix = form.device_type === "lampara_uv"
        ? "UV"
        : { verde: "V", amarilla: "A", roja: "R" }[form.zone];

    const count = Math.max(1, Math.min(50, parseInt(form.count, 10) || 1));
    const subtypeMap = SUBTYPES_FOR(form.device_type);
    const showSubtype = !!subtypeMap;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="place-device-dialog">
                <DialogHeader><DialogTitle>Nuevo{count > 1 ? "s" : ""} dispositivo{count > 1 ? "s" : ""}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Zona</Label>
                        <Select value={form.zone} onValueChange={setZone}>
                            <SelectTrigger data-testid="device-zone-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ZONES.map((z) => <SelectItem key={z.v} value={z.v}>{z.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tipo de dispositivo</Label>
                        <Select value={form.device_type} onValueChange={setDeviceType}>
                            <SelectTrigger data-testid="device-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {allowedTypes.map((t) => <SelectItem key={t} value={t}>{TYPES[t]}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {showSubtype && (
                        <div>
                            <Label>{form.device_type === "trampa" ? "Subtipo de trampa" : "Subtipo de cebadero"}</Label>
                            <Select value={form.trap_subtype} onValueChange={(v) => setForm({ ...form, trap_subtype: v })}>
                                <SelectTrigger data-testid="device-subtype-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.entries(subtypeMap).map(([k, l]) => (
                                        <SelectItem key={k} value={k}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label>Cantidad <span className="text-muted-foreground text-xs">(máx 50)</span></Label>
                        <Input type="number" min={1} max={50}
                            value={form.count}
                            onChange={(e) => setForm({ ...form, count: e.target.value })}
                            data-testid="device-count-input" />
                    </div>
                    <div>
                        <Label>Área / Ubicación común</Label>
                        <AreaSelector
                            value={form.area_name}
                            onChange={(v) => setForm({ ...form, area_name: v })}
                            areaNames={areaNames}
                            testId="device-area-select"
                        />
                    </div>
                    <div className="bg-slate-50 border rounded p-2 text-xs">
                        {count === 1 ? (
                            <>
                                <span className="text-muted-foreground">Código QR auto-generado:</span>{" "}
                                <b className="font-mono">{previewPrefix}### </b>
                                {showSubtype && <span className="text-muted-foreground">· {subtypeMap[form.trap_subtype]}</span>}
                            </>
                        ) : (
                            <>
                                <span className="text-muted-foreground">Se crearán</span>{" "}
                                <b>{count}</b>{" "}
                                <span className="text-muted-foreground">dispositivos:</span>{" "}
                                <b className="font-mono">{previewPrefix}###</b>
                                <span className="text-muted-foreground"> · </span>
                                <b className="font-mono">{previewPrefix}###</b>
                                <span className="text-muted-foreground"> · ... (correlativos)</span>
                                {showSubtype && (
                                    <div className="text-muted-foreground mt-1">Subtipo: <b>{subtypeMap[form.trap_subtype]}</b></div>
                                )}
                                <div className="text-muted-foreground mt-1">Aparecerán en un pequeño grid; usa "Mover" para acomodarlos.</div>
                            </>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => onSave(form)} data-testid="device-save-btn">
                        <Save className="w-4 h-4 mr-1" /> Crear {count > 1 ? `${count}` : ""}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditDeviceDialog({ device, onClose, onSave, onDelete, areaNames, areas }) {
    const [form, setForm] = useState(() => {
        if (!device) return { qr_code: "", device_type: "cebadero_cebo", zone: "verde", trap_subtype: "caja", area_name: "", status: "active" };
        // Auto-detect area if not already assigned
        let areaName = device.area_name || "";
        if (!areaName && areas && device.position_x != null && device.position_y != null) {
            areaName = detectAreaAtPoint(areas, device.position_x, device.position_y);
        }
        return {
            qr_code: device.qr_code,
            device_type: device.device_type,
            zone: device.zone,
            trap_subtype: device.trap_subtype || DEFAULT_SUBTYPE_FOR(device.device_type),
            area_name: areaName,
            status: device.status || "active",
        };
    });
    const allowedTypes = ZONES.find((z) => z.v === form.zone)?.allow || [];

    const setZone = (zone) => {
        const allow = ZONES.find((z) => z.v === zone)?.allow || [];
        const nextType = allow.includes(form.device_type) ? form.device_type : allow[0];
        setForm((s) => ({ ...s, zone, device_type: nextType, trap_subtype: DEFAULT_SUBTYPE_FOR(nextType) }));
    };

    const setDeviceType = (v) => {
        setForm((s) => ({ ...s, device_type: v, trap_subtype: DEFAULT_SUBTYPE_FOR(v) }));
    };

    const subtypeMap = SUBTYPES_FOR(form.device_type);
    const showSubtype = !!subtypeMap;
    if (!device) return null;
    return (
        <Dialog open={!!device} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md w-[90vw]" data-testid="edit-device-dialog">
                <DialogHeader><DialogTitle>{device.device_number}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Zona</Label>
                        <Select value={form.zone} onValueChange={setZone}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{ZONES.map((z) => <SelectItem key={z.v} value={z.v}>{z.l}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tipo</Label>
                        <Select value={form.device_type} onValueChange={setDeviceType}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{allowedTypes.map((t) => <SelectItem key={t} value={t}>{TYPES[t]}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    {showSubtype && (
                        <div>
                            <Label>{form.device_type === "trampa" ? "Subtipo de trampa" : "Subtipo de cebadero"}</Label>
                            <Select value={form.trap_subtype || DEFAULT_SUBTYPE_FOR(form.device_type)} onValueChange={(v) => setForm({ ...form, trap_subtype: v })}>
                                <SelectTrigger data-testid="edit-device-subtype-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {Object.entries(subtypeMap).map(([k, l]) => (
                                        <SelectItem key={k} value={k}>{l}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label>Código QR</Label>
                        <Input value={form.qr_code} onChange={(e) => setForm({ ...form, qr_code: e.target.value })} />
                    </div>
                    <div>
                        <Label>Área</Label>
                        <AreaSelector
                            value={form.area_name || ""}
                            onChange={(v) => setForm({ ...form, area_name: v })}
                            areaNames={areaNames}
                            testId="edit-device-area-select"
                        />
                    </div>
                    <div>
                        <Label>Estado</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="active">Activo</SelectItem>
                                <SelectItem value="inactive">Inactivo</SelectItem>
                                <SelectItem value="damaged">Dañado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter className="flex items-center gap-2">
                    <Button variant="outline" className="text-red-600" onClick={onDelete} data-testid="device-delete-btn">
                        <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => onSave(form)} data-testid="device-save-edit-btn">
                        <Save className="w-4 h-4 mr-1" /> Guardar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
